import React, { useEffect, useState } from 'react';
import useUiContext from '@groupefungo/react-apollo.core/ui/UseContext';
import * as PropTypes from 'prop-types';
import useAppContext from '@groupefungo/react-apollo.core/app/UseContext';
import ReactPixel from 'react-facebook-pixel';
import { PayPalButtons } from '@paypal/react-paypal-js';
import useClientsEventBookingContext from './hooks/useClientsEventBookingContext';
import useClientsEventBookingFormMutation from './hooks/useClientsEventBookingFormMutation';
import useCurrentClientProfileContext from '../ui/useCurrentClientProfileContext';
import { formatDatetime, formattedDate } from '../../../../../base/utils/parser';
import CountdownTimer from '../../../../../base/utils/countdownTimer';

function AppointmentConfirmation({ confirmed, proceedTo, canceledLink }) {
  const amount = 50;
  const [canceling, setCanceling] = useState(false);
  const { Grid, Button } = useUiContext();
  const { useRouter } = useAppContext();
  const { pushPath } = useRouter();
  const { clientsEventBooking, setProceedTo } = useClientsEventBookingContext();
  const { mutate, cancelBooking } = useClientsEventBookingFormMutation();

  useEffect(() => {
    if (proceedTo) {
      setProceedTo(proceedTo);
    }
  }, [proceedTo, setProceedTo]);

  const {
    id: clientsEventId,
    gid,
    roleName,
    upcomingAppointment: {
      id: appointmentId, gid: appointmentGid, appointmentType, dressType,
      usersAvailability: {
        user: {
          fullName,
        },
        statusTimestamp,
        availability: {
          from: dateFrom,
        },
      },
      confirmation: { id: confirmationId, confirmed: appointmentConfirmed },
    },
    clientAttributes: { gid: clientGid, firstName, lastName, email: clientEmail, bestPhoneNumber: { phoneNo, phoneType } },
    event: { id: eventId, gid: eventGid, eventDateFormatted, eventDate, budget },
  } = clientsEventBooking;

  const getConfirmedInput = (isConfirmed) => ({
    id: clientsEventId,
    appointmentsAttributes: [{
      id: appointmentId,
      status: 'Pending', // Appointment Status is Pending up until four days prior confirmation
      confirmationAttributes: {
        id: confirmationId,
        confirmed: isConfirmed,
        confirmedThroughStore: true,
      },
    }],
    eventAttributes: {
      eventTitle: `${firstName}'s event`,
      eventDate,
      eventType: '-1',
      id: eventId,
      budget,
      assignedPerson: fullName,
      statusCategory: 'First Appointment Scheduled',
    },
  });

  const handleSetCanceling = (isCanceling) => () => {
    setCanceling(isCanceling);
  };

  const handleCancel = () => {
    cancelBooking(appointmentGid, canceledLink);
  };

  const handleConfirm = () => {
    mutate(getConfirmedInput(true), { canProceed: true });
  };

  const Title = () => (
    <div className="text-container">
      <h1>To confirm your appointment</h1>
      <p>An amount of ${amount} will be pre-authorized on your account. You will not be charged if you change or cancel with at least 24 hours notice.</p>

      <p> Once completed, you will receive a confirmation email of your appointment.</p>
    </div>
  );

  const [orderID, setOrderID] = useState(false);

  function createOrder(data, actions) {
    return actions.order
      .create({
        intent: 'AUTHORIZE',
        purchase_units: [
          {
            amount: {
              value: amount,
            },
          },
        ],
      })
      // eslint-disable-next-line no-shadow
      .then((orderID) => {
        setOrderID(orderID);
        return orderID;
      });
  }

  const handleContinue = (data, actions) => actions.order.authorize().then(() => {
    // Add details in the function to access the details access for any persistence of data
    handleConfirm();
    pushPath(proceedTo);
  });

  const handleError = (e) => {
    console.log(e);
  };

  window.addEventListener('beforeunload', (e) => {
    e.returnValue = 'Form is not complete. Are you sure you want to leave?';
  });

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      const script = document.createElement('script');

      script.src = 'https://181302.t.hyros.com/v1/lst/universal-script?ph=9ce99df8788751883d56ec077d783be0e2385d3244728ba58b2543d3f3c3fb7b&tag=!clicked';

      script.async = true;

      document.body.appendChild(script);

      return () => {
        // clean up the script when the component in unmounted
        document.body.removeChild(script);
      };
    }
  }, []);

  useEffect(() => {
    if (appointmentConfirmed) {
      pushPath(proceedTo);
    }
  }, []);

  if (canceling) {
    return (
      <div className="appointment-cancel-state">
        <div className="text-container">
          <h1>Are you sure you want to <br /> cancel your appointment?</h1>
        </div>
        <Grid container>
          <div className="form-btn">
            <div className="stacked-buttons">
              <Button className="Confirm" onClick={handleCancel}>Yes, cancel my appointment.</Button>
              <Button onClick={handleSetCanceling(false)}>Back</Button>
            </div>
          </div>
        </Grid>
      </div>
    );
  }

  const renderConfirmationFBPixel = () => {
    if (process.env.NODE_ENV === 'production') {
      ReactPixel.init('527878900693629');
      ReactPixel.track('Schedule');
    }
  };

  const Buttons = () => [
    { label: 'Confirm Booking', onClick: handleConfirm, color: 'primary' },
    { label: 'Cancel', onClick: handleSetCanceling(true), color: 'secondary' },
  ].map(({ label, onClick, color }) => <Button key={`btn-${label}`} className={label} onClick={onClick} color={color}>{label}</Button>);

  const handleOnShippingChange = (e) => {
    console.log(e);
  };

  return (
    <div className="appointment-confirmation-wrapper">
      <CountdownTimer offset={30} statusTimestamp={statusTimestamp} gid={gid} />
      <Grid container justify="center">
        <Grid item md={6} className="left-item">
          <Title />
          <div className="form-btn">
            <div className="text-container">
              <h2 className="payment-method">Payment Method</h2>
            </div>
            <div className="stacked-buttons">
              {process.env.NODE_ENV === 'development' ? <Buttons />
                : (
                  <PayPalButtons
                    createOrder={createOrder}
                    onApprove={handleContinue}
                    onError={handleError}
                    onShippingChange={handleOnShippingChange}
                    style={{ color: 'blue', shape: 'rect', label: 'pay', height: 40 }}
                  />
                )}
            </div>
          </div>
        </Grid>
        <Grid item xs={12} md={6} className="confirmation-content">
          <Grid container>
            <Grid item xs={12} md={6}>
              <h2>Party Name</h2>
              <p>{firstName} {lastName}</p>
              <h2>You are</h2>
              <p>{roleName}</p>
              <h2>Email</h2>
              <p>{clientEmail}</p>
              <h2>For dress</h2>
              <p>{dressType}</p>
            </Grid>
            <Grid item xs={12} md={6} className="confirmation-panel">
              <h2>Date of appointment</h2>
              <p>{formatDatetime(formattedDate(dateFrom))}</p>
              <h2>Appointment Type</h2>
              <p>{appointmentType}</p>
              <h2>Phone Number</h2>
              <p>{phoneNo}&nbsp;&nbsp;{phoneType}</p>
              <h2>Event Date</h2>
              <p>{eventDateFormatted}</p>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container justify="center">
        <Grid item xs={12} sm={12} md={12} lg={12}>
          {orderID
          && (
          <div className="confirmation-content">
            <h2>Order ID: {orderID}</h2>
          </div>
          )}
        </Grid>
      </Grid>
      {confirmed && renderConfirmationFBPixel()}
    </div>
  );
}

AppointmentConfirmation.defaultProps = {
  confirmed: false,
  modifyLink: '',
  proceedTo: '',
  canceledLink: '',
};
AppointmentConfirmation.propTypes = {
  confirmed: PropTypes.bool,
  modifyLink: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  canceledLink: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  proceedTo: PropTypes.string,
};
export default AppointmentConfirmation;
